import React from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { 
  TrendingUp, TrendingDown, Target, DollarSign, 
  Calendar, BarChart3, PieChart, AlertTriangle
} from 'lucide-react';

const formatCurrency = (value) => {
  if (value >= 1000000) return `R${(value / 1000000).toFixed(1)}M`;
  if (value >= 1000) return `R${(value / 1000).toFixed(0)}K`;
  return `R${value?.toFixed(0) || 0}`;
};

const RevenueIntelligence = ({ dashboard, opportunities }) => {
  // Calculate additional metrics
  const activeOpps = opportunities?.filter(o => !['Awarded', 'Lost'].includes(o.stage)) || [];
  const wonOpps = opportunities?.filter(o => o.stage === 'Awarded') || [];
  const lostOpps = opportunities?.filter(o => o.stage === 'Lost') || [];
  
  // Pipeline by stage
  const stageData = dashboard?.opportunities_by_stage || [];
  
  // Calculate pipeline health indicators
  const staleDealCount = activeOpps.filter(o => o.days_in_stage > 30).length;
  const atRiskValue = activeOpps
    .filter(o => o.days_in_stage > 30)
    .reduce((sum, o) => sum + (o.estimated_value || 0), 0);
  
  // Calculate win/loss trend (simplified - would need historical data for real trend)
  const winRate = dashboard?.conversion_rate || 0;
  const avgDealCycle = wonOpps.length > 0 
    ? Math.round(wonOpps.reduce((sum, o) => sum + (o.days_in_stage || 30), 0) / wonOpps.length)
    : 0;
  
  // Service type breakdown
  const serviceTypeBreakdown = activeOpps.reduce((acc, opp) => {
    const type = opp.service_type || 'Other';
    if (!acc[type]) acc[type] = { count: 0, value: 0 };
    acc[type].count += 1;
    acc[type].value += opp.estimated_value || 0;
    return acc;
  }, {});

  // Forecast next 3 months (simplified calculation)
  const closeThisMonth = activeOpps.filter(o => {
    if (!o.expected_close_date) return false;
    const closeDate = new Date(o.expected_close_date);
    const now = new Date();
    return closeDate.getMonth() === now.getMonth() && closeDate.getFullYear() === now.getFullYear();
  });
  
  const forecastThisMonth = closeThisMonth.reduce((sum, o) => 
    sum + ((o.estimated_value || 0) * (o.probability || 0) / 100), 0);

  return (
    <div className="p-6 space-y-6">
      {/* Header */}
      <div>
        <h2 className="text-lg font-semibold text-slate-900">Revenue Intelligence</h2>
        <p className="text-sm text-slate-500">Pipeline analytics and forecasting</p>
      </div>

      {/* Key Metrics Row */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <Card>
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-xs text-slate-500 uppercase">Pipeline Value</p>
                <p className="text-2xl font-bold text-slate-900">{formatCurrency(dashboard?.pipeline_value || 0)}</p>
                <p className="text-xs text-slate-400">{activeOpps.length} active deals</p>
              </div>
              <Target className="h-8 w-8 text-indigo-500" />
            </div>
          </CardContent>
        </Card>
        
        <Card>
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-xs text-slate-500 uppercase">Weighted Forecast</p>
                <p className="text-2xl font-bold text-slate-900">{formatCurrency(dashboard?.weighted_pipeline || 0)}</p>
                <p className="text-xs text-green-600">Probability adjusted</p>
              </div>
              <TrendingUp className="h-8 w-8 text-green-500" />
            </div>
          </CardContent>
        </Card>
        
        <Card>
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-xs text-slate-500 uppercase">Win Rate</p>
                <p className="text-2xl font-bold text-slate-900">{winRate.toFixed(0)}%</p>
                <p className="text-xs text-slate-400">{wonOpps.length} won / {wonOpps.length + lostOpps.length} closed</p>
              </div>
              <PieChart className="h-8 w-8 text-blue-500" />
            </div>
          </CardContent>
        </Card>
        
        <Card>
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-xs text-slate-500 uppercase">Avg Deal Size</p>
                <p className="text-2xl font-bold text-slate-900">{formatCurrency(dashboard?.avg_deal_size || 0)}</p>
                <p className="text-xs text-slate-400">Won deals</p>
              </div>
              <DollarSign className="h-8 w-8 text-amber-500" />
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Pipeline Health & Forecast */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        {/* Pipeline by Stage */}
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium flex items-center gap-2">
              <BarChart3 className="h-4 w-4" />
              Pipeline by Stage
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-3">
              {stageData.filter(s => !['Awarded', 'Lost'].includes(s.stage)).map((stage, idx) => {
                const maxValue = Math.max(...stageData.map(s => s.total_value || 0));
                const percentage = maxValue > 0 ? ((stage.total_value || 0) / maxValue) * 100 : 0;
                const colors = ['bg-slate-400', 'bg-blue-500', 'bg-indigo-500', 'bg-purple-500', 'bg-amber-500'];
                
                return (
                  <div key={stage.stage}>
                    <div className="flex items-center justify-between mb-1">
                      <span className="text-sm text-slate-600">{stage.stage}</span>
                      <span className="text-sm font-medium">{formatCurrency(stage.total_value || 0)}</span>
                    </div>
                    <div className="h-2 bg-slate-100 rounded-full overflow-hidden">
                      <div 
                        className={`h-full ${colors[idx % colors.length]} transition-all duration-500`}
                        style={{ width: `${percentage}%` }}
                      />
                    </div>
                    <div className="flex justify-between text-xs text-slate-400 mt-1">
                      <span>{stage.count} deals</span>
                      <span>Weighted: {formatCurrency(stage.weighted_value || 0)}</span>
                    </div>
                  </div>
                );
              })}
            </div>
          </CardContent>
        </Card>

        {/* Pipeline Health */}
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium flex items-center gap-2">
              <AlertTriangle className="h-4 w-4" />
              Pipeline Health
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-4">
              {/* At Risk Deals */}
              <div className="p-3 bg-amber-50 rounded-lg border border-amber-200">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm font-medium text-amber-800">Stale Deals ({">"}30 days)</p>
                    <p className="text-xs text-amber-600">{staleDealCount} deals at risk</p>
                  </div>
                  <div className="text-right">
                    <p className="text-lg font-bold text-amber-700">{formatCurrency(atRiskValue)}</p>
                    <p className="text-xs text-amber-600">Value at risk</p>
                  </div>
                </div>
              </div>

              {/* Monthly Stats */}
              <div className="grid grid-cols-2 gap-3">
                <div className="p-3 bg-green-50 rounded-lg border border-green-200">
                  <p className="text-xs text-green-600 uppercase">Won This Month</p>
                  <p className="text-xl font-bold text-green-700">{formatCurrency(dashboard?.won_value_this_month || 0)}</p>
                  <p className="text-xs text-green-600">{dashboard?.won_this_month || 0} deals</p>
                </div>
                <div className="p-3 bg-red-50 rounded-lg border border-red-200">
                  <p className="text-xs text-red-600 uppercase">Lost This Month</p>
                  <p className="text-xl font-bold text-red-700">{dashboard?.lost_this_month || 0}</p>
                  <p className="text-xs text-red-600">deals lost</p>
                </div>
              </div>

              {/* Forecast */}
              <div className="p-3 bg-indigo-50 rounded-lg border border-indigo-200">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm font-medium text-indigo-800">This Month Forecast</p>
                    <p className="text-xs text-indigo-600">{closeThisMonth.length} deals expected to close</p>
                  </div>
                  <div className="text-right">
                    <p className="text-lg font-bold text-indigo-700">{formatCurrency(forecastThisMonth)}</p>
                    <p className="text-xs text-indigo-600">Weighted value</p>
                  </div>
                </div>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Service Type Breakdown */}
      <Card>
        <CardHeader className="pb-2">
          <CardTitle className="text-sm font-medium">Pipeline by Service Type</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            {Object.entries(serviceTypeBreakdown).map(([type, data]) => (
              <div key={type} className="p-3 bg-slate-50 rounded-lg">
                <p className="text-sm font-medium text-slate-700">{type}</p>
                <p className="text-lg font-bold text-slate-900">{formatCurrency(data.value)}</p>
                <p className="text-xs text-slate-500">{data.count} deals</p>
              </div>
            ))}
            {Object.keys(serviceTypeBreakdown).length === 0 && (
              <p className="col-span-full text-sm text-slate-400 text-center py-4">No active deals</p>
            )}
          </div>
        </CardContent>
      </Card>

      {/* Tasks & Activities Summary */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <Card>
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-xs text-slate-500 uppercase">Overdue Tasks</p>
                <p className="text-2xl font-bold text-red-600">{dashboard?.overdue_tasks || 0}</p>
                <p className="text-xs text-slate-400">Require attention</p>
              </div>
              <Calendar className="h-8 w-8 text-red-400" />
            </div>
          </CardContent>
        </Card>
        
        <Card>
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-xs text-slate-500 uppercase">Activities This Week</p>
                <p className="text-2xl font-bold text-slate-900">{dashboard?.activities_this_week || 0}</p>
                <p className="text-xs text-slate-400">Logged interactions</p>
              </div>
              <TrendingUp className="h-8 w-8 text-green-500" />
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default RevenueIntelligence;
